/**
 * StudyDeviceConfig - Device configuration tab for Study Details page (FECOA-698)
 *
 * Allows Study Managers to configure:
 * - Device strategy (BYOD, Provisioned, Both)
 * - Allowed device types (Phone, Tablet, Desktop)
 * - Provisioned device selection (when strategy includes provisioned)
 *
 * TODO: Eventually this should be part of a more formalized study configuration
 * page with sections/tabs for all study settings (device, portal, participant IDs, etc.)
 * rather than living as a standalone tab on the Study Details page.
 * See also: EditStudy.jsx which currently handles most of these settings in a long form.
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Monitor, Smartphone, Tablet, Save, Loader2, Info } from 'lucide-react';
import studyService from '../../services/studyService';
import api from '../../services/api';
import PageLoader from '../ui/PageLoader';

const STRATEGY_OPTIONS = [
  {
    value: 'byod',
    label: 'Bring Your Own Device (BYOD)',
    description: 'Participants use their personal devices to complete assessments',
  },
  {
    value: 'provisioned',
    label: 'Provisioned Devices',
    description: 'Participants are given specific devices for the study',
  },
  {
    value: 'both',
    label: 'Both',
    description: 'Participants may use personal or provisioned devices',
  },
];

const DEVICE_TYPE_OPTIONS = [
  { value: 'phone', label: 'Phone', icon: Smartphone },
  { value: 'tablet', label: 'Tablet', icon: Tablet },
  { value: 'desktop', label: 'Desktop', icon: Monitor },
];

const StudyDeviceConfig = ({ studyID, canEdit = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deviceStrategy, setDeviceStrategy] = useState('byod');
  const [allowedDeviceTypes, setAllowedDeviceTypes] = useState([]);
  const [provisionedDeviceIds, setProvisionedDeviceIds] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Store initial values to detect changes
  const [initial, setInitial] = useState({});

  useEffect(() => {
    loadData();
  }, [studyID]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [studyData, devicesResponse] = await Promise.all([
        studyService.getSingleStudy(studyID),
        api.get('/api/assessment-builder/devices/'),
      ]);

      const strategy = studyData.device_strategy || 'byod';
      const types = studyData.allowed_device_types || [];
      const provIds = (studyData.provisioned_devices || []).map(d => d.id);

      setDeviceStrategy(strategy);
      setAllowedDeviceTypes(types);
      setProvisionedDeviceIds(provIds);
      setInitial({ strategy, types, provIds });

      // Devices endpoint returns { devices: [...], grouped: {...}, count: N }
      const devices = devicesResponse.data?.devices || devicesResponse.data?.results || [];
      setAvailableDevices(Array.isArray(devices) ? devices : []);
    } catch (error) {
      console.error('Error loading device config:', error);
      toast.error('Failed to load device configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const changed =
      deviceStrategy !== initial.strategy ||
      JSON.stringify(allowedDeviceTypes.sort()) !== JSON.stringify((initial.types || []).sort()) ||
      JSON.stringify(provisionedDeviceIds.sort()) !== JSON.stringify((initial.provIds || []).sort());
    setHasChanges(changed);
  }, [deviceStrategy, allowedDeviceTypes, provisionedDeviceIds, initial]);

  const handleDeviceTypeToggle = (type) => {
    setAllowedDeviceTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleProvisionedDeviceToggle = (deviceId) => {
    setProvisionedDeviceIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await studyService.updateStudy(studyID, {
        device_strategy: deviceStrategy,
        allowed_device_types: allowedDeviceTypes,
        provisioned_device_ids: provisionedDeviceIds,
        // Keep is_byod_allowed in sync for backward compatibility
        is_byod_allowed: deviceStrategy === 'byod' || deviceStrategy === 'both',
      });
      setInitial({
        strategy: deviceStrategy,
        types: [...allowedDeviceTypes],
        provIds: [...provisionedDeviceIds],
      });
      setHasChanges(false);
      toast.success('Device configuration saved');
    } catch (error) {
      console.error('Error saving device config:', error);
      toast.error('Failed to save device configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader className="py-16" />;
  }

  const showProvisioned = deviceStrategy === 'provisioned' || deviceStrategy === 'both';
  const showAllowedTypes = deviceStrategy === 'byod' || deviceStrategy === 'both';

  const optionClass = (selected) => (selected
    ? 'border-primary-500 bg-primary-50/60 ring-1 ring-primary-500 dark:border-primary-500 dark:bg-primary-400/10 dark:ring-primary-500'
    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Device Strategy */}
        <section>
          <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">Device Strategy</h3>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Choose how participants will access assessments in this study
          </p>
          <div className="space-y-3">
            {STRATEGY_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex cursor-pointer items-start rounded-xl border p-4 transition-colors ${optionClass(deviceStrategy === option.value)} ${!canEdit ? 'pointer-events-none opacity-75' : ''}`}
              >
                <input
                  type="radio"
                  name="deviceStrategy"
                  value={option.value}
                  checked={deviceStrategy === option.value}
                  onChange={(e) => setDeviceStrategy(e.target.value)}
                  disabled={!canEdit}
                  className="mt-0.5 h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{option.label}</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Allowed Device Types */}
        {showAllowedTypes && (
          <section>
            <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">Allowed Device Types</h3>
            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
              Select which <strong className="font-semibold text-gray-700 dark:text-gray-200">categories</strong> of devices participants can use to complete assessments.
            </p>
            <p className="mb-4 text-xs text-gray-400 dark:text-gray-500">
              This controls which canvas sizes are available in the Assessment Builder and which device types participants can access the study from. Leave all unchecked to allow any device.
            </p>
            <div className="grid grid-cols-3 gap-4">
              {DEVICE_TYPE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = allowedDeviceTypes.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => canEdit && handleDeviceTypeToggle(option.value)}
                    disabled={!canEdit}
                    aria-pressed={isSelected}
                    className={`flex flex-col items-center rounded-xl border p-5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/60 ${optionClass(isSelected)} ${isSelected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'} ${!canEdit ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                  >
                    <Icon size={24} className="mb-2" />
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                );
              })}
            </div>
            {allowedDeviceTypes.length === 0 && (
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Info size={16} />
                <span>No restrictions - all device types are allowed</span>
              </div>
            )}
          </section>
        )}

        {/* Provisioned Devices */}
        {showProvisioned && (
          <section>
            <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">Provisioned Devices</h3>
            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
              Select the <strong className="font-semibold text-gray-700 dark:text-gray-200">specific device models</strong> that will be physically provided to participants for this study.
            </p>
            <p className="mb-4 text-xs text-gray-400 dark:text-gray-500">
              This is for tracking which hardware is assigned to the study, not for restricting access. Use "Allowed Device Types" above to control which device categories are permitted.
            </p>
            {availableDevices.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
                No devices have been configured in the system yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {availableDevices
                  .filter(d => d.is_active !== false)
                  .map((device) => {
                    const isSelected = provisionedDeviceIds.includes(device.id);
                    const TypeIcon =
                      device.device_type === 'phone' ? Smartphone :
                      device.device_type === 'tablet' ? Tablet : Monitor;
                    return (
                      <label
                        key={device.id}
                        className={`flex items-center rounded-xl border p-3 transition-colors ${optionClass(isSelected)} ${!canEdit ? 'pointer-events-none opacity-75' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleProvisionedDeviceToggle(device.id)}
                          disabled={!canEdit}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <TypeIcon size={16} className="ml-3 text-gray-400 dark:text-gray-500" />
                        <div className="ml-2 min-w-0">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{device.name}</span>
                          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                            {device.screen_width}x{device.screen_height}
                          </span>
                        </div>
                      </label>
                    );
                  })}
              </div>
            )}
          </section>
        )}

        {/* Save Button */}
        {canEdit && (
          <div className="flex justify-end border-t border-gray-100 pt-4 dark:border-gray-700">
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="btn btn-primary"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Device Configuration
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default StudyDeviceConfig;
